import React from 'react';
import { Box, Typography, InputLabel, Tooltip, FormControl, Select, MenuItem, Alert, Button, CircularProgress } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

const MolecularSettingsTab = ({ tabProps }) => {
  const {
    molecularDirty,
    molecularError,
    setMolecularError,
    tooltipProps,
    infoIconStyle,
    defaultInterpretationCount,
    handleInterpretationCountChange,
    reviewThreshold,
    setReviewThreshold,
    defaultHgvsRef,
    setDefaultHgvsRef,
    validationErrors,
    renderComboboxes,
    stickyActionBarSx,
    renderDisabledInfoIcon,
    handleCancelMolecular,
    molecularSaving,
    onSubmitMolecular,
  } = tabProps;

  return (
  <Box className="psTabContent">
    <Typography variant="h6" component="h2" className="psTabTitle">Molecular Module Settings</Typography>
    {molecularDirty && (
      <Typography variant="caption" sx={{ color: 'var(--accent-primary-color)', fontWeight: 500, display: 'block', textAlign: 'right' }}>
        * Unsaved changes
      </Typography>
    )}

    {molecularError && (
      <Alert severity="error" sx={{ mb: 1 }} onClose={() => setMolecularError('')}>
        {molecularError}
      </Alert>
    )}

    <Box className="psFormGrid" sx={{ mt: 3 }}>
      <Box className="psFormRow">
        <Box className="psFormLabel">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InputLabel
              id="numberOfInterpreters-label"
              htmlFor="numberOfInterpreters"
              sx={{ position: 'static', transform: 'none', color: 'inherit', fontSize: '1rem', whiteSpace: 'normal' }}
              shrink
            >
              Default Number of Required Interpretations:
            </InputLabel>
            <Tooltip
              title="This setting sets the default number of interpretations required to be submitted for a newly created variant review."
              {...tooltipProps}
            >
              <span tabIndex={0} role="button" aria-label="Information about default number of required interpretations">
                <InfoOutlined style={infoIconStyle} />
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box className="psFormInput">
          <FormControl variant="outlined" size="small">
            <Select
              labelId="numberOfInterpreters-label"
              id="numberOfInterpreters"
              name="numberOfInterpreters"
              value={defaultInterpretationCount || ''}
              onChange={handleInterpretationCountChange}
              MenuProps={{
                disableScrollLock: true,
                disablePortal: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              {[1, 2, 3, 4, 5].map(value => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box className="psFormRow">
        <Box className="psFormLabel">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InputLabel
              id="reviewThreshold-label"
              htmlFor="reviewThreshold"
              sx={{ position: 'static', transform: 'none', color: 'inherit', fontSize: '1rem', whiteSpace: 'normal' }}
              shrink
            >
              Default Review Threshold (days):
            </InputLabel>
            <Tooltip
              title="This setting sets the default minimum number of days since the last review for a variant that a new review will be created automatically when that variant is identified again in a patient by the program."
              {...tooltipProps}
            >
              <span tabIndex={0} role="button" aria-label="Information about default review threshold">
                <InfoOutlined style={infoIconStyle} />
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box className="psFormInput">
          <FormControl variant="outlined" size="small">
            <Select
              labelId="reviewThreshold-label"
              id="reviewThreshold"
              name="reviewThreshold"
              value={reviewThreshold || ''}
              onChange={(e) => setReviewThreshold(e.target.value)}
              MenuProps={{
                disableScrollLock: true,
                disablePortal: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              {[180, 120, 90, 60, 30].map(value => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box className="psFormRow psFormRowInterpreters">
        <Box className="psFormLabel">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Default Assigned Interpreters:
            </Typography>
            <Tooltip
              title="This setting determines which users will automatically be assigned interpretations when a new variant review is created. The number of interpreters specified here cannot exceed the number specified in Default Number of Required Interpretations."
              {...tooltipProps}
            >
              <span tabIndex={0} role="button" aria-label="Information about default assigned interpreters">
                <InfoOutlined style={infoIconStyle} />
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box className="psFormInput">
          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {validationErrors.map((message, index) => (
                <div key={index}>{message}</div>
              ))}
            </Alert>
          )}
          {renderComboboxes()}
        </Box>
      </Box>

      <Box className="psFormRow">
        <Box className="psFormLabel">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InputLabel
              id="defaultHgvsRef-label"
              htmlFor="defaultHgvsRef"
              sx={{ position: 'static', transform: 'none', color: 'inherit', fontSize: '1rem', whiteSpace: 'normal' }}
              shrink
            >
              Default HGVS Reference Display:
            </InputLabel>
            <Tooltip
              title="Select the default HGVS reference
                      system to use when referring to
                      variants throughout the ED3N
                      platform."
              {...tooltipProps}
            >
              <span tabIndex={0} role="button" aria-label="Information about default HGVS reference display">
                <InfoOutlined style={infoIconStyle} />
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box className="psFormInput">
          <FormControl variant="outlined" size="small">
            <Select
              labelId="defaultHgvsRef-label"
              id="defaultHgvsRef"
              name="defaultHgvsRef"
              value={defaultHgvsRef || 'NC'}
              onChange={(e) => setDefaultHgvsRef(e.target.value)}
              MenuProps={{
                disableScrollLock: true,
                disablePortal: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem value="NC">Chromosomal Reference Name (NC_)</MenuItem>
              <MenuItem value="NM">Transcripts Reference Name (NM_)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>

    <Box sx={stickyActionBarSx}>
      {!molecularDirty && renderDisabledInfoIcon('No changes to apply')}
      <Button
        variant="outlined"
        onClick={handleCancelMolecular}
        disabled={!molecularDirty || molecularSaving}
        sx={{ mr: 1 }}
      >
        Cancel
      </Button>
      <Button
        variant={!molecularDirty || molecularSaving ? 'outlined' : 'contained'}
        color="primary"
        onClick={onSubmitMolecular}
        disabled={!molecularDirty || molecularSaving}
        startIcon={molecularSaving ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {molecularSaving ? 'Applying...' : 'Apply'}
      </Button>
    </Box>
  </Box>
  );
};

export default MolecularSettingsTab;
